import useApiHook from "@hooks/useApiHook";
import routes from "@routes/routes";
import useLoader from "@hooks/useLoader";
import React, { useCallback, useEffect, useRef, useState } from "react";
import TransparentLoader from "@components/Loaders/TransparentLoader";
import checkbox from "@assets/images/mdi_check-bold.webp";
import useApiError from "@hooks/useApiError";
import {
  checkIfContainsSpecialCharacter,
  containsLowerCase,
  containsNumber,
  containsUpperCase,
  isLengthGreaterThanOrEqualToEight,
} from "@utils/PasswordHelpers";
import swal from "sweetalert2";
import CustomDatePicker from "./CustomDatePicker";
import { subYears, isAfter, isEqual } from "date-fns";
import useNotification from "@src/hooks/useNotification";
import { emailRegex } from "@src/utils/Regex";
import inputBgMd from "@assets/images/backgrounds/inputBgMd.webp";
import { useSearchParams } from "react-router-dom";
import useIsMobile from "@src/hooks/useIsMobile";
import "@src/styles/SignupStyle.scss";
import type { ISignup, IUser } from "@src/types/AppTypes";

const Signup = React.forwardRef<HTMLDivElement, ISignup>(
  ({ signUpModal, modalBorder, setSignUpModal, setLoginModal }, ref) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const isMobile = useIsMobile();
    const [passwordDropdown, setPasswordDropdown] = useState<boolean>(false);
    const passwordRef = useRef<HTMLInputElement>(null);
    const [passwordsMatch, setPasswordsMatch] = useState<boolean>(true);
    const setApiError = useApiError();
    const flashNotification = useNotification();
    const { isLoading, loaderDispatch } = useLoader();
    const signUpEndPoint = useApiHook({
      url: "/users/registrations",
      method: "post",
    });
    const checkEmailEnpoint = useApiHook({
      url: "/users/check_emails",
      method: "post",
    });
    const [first_name, setFirst_name] = useState<string>("");
    const [last_name, setLast_name] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [dob, setDob] = useState<Date | null>(null);
    const [password_confirmation, setPassword_confirmation] =
      useState<string>("");
    const [is_confirm_parental, setIs_confirm_parental] =
      useState<boolean>(false);
    const [is_agree_terms, setIs_agree_terms] = useState<boolean>(false);
    const [hasEightChars, setHasEightChars] = useState<boolean>(false);
    const [hasSpecialChar, setHasSpecialChar] = useState<boolean>(false);
    const [hasLowerAndUpperChar, setHasLowerAndUpperChar] =
      useState<boolean>(false);
    const [hasNumber, setHasNumber] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [showConfirmPassword, setShowConfirmPassword] =
      useState<boolean>(false);
    const [isPasswordValid, setIsPasswordValid] = useState<boolean>(true);
    const [error, setError] = useState<{ type: "email"; value: string } | null>(
      null
    );

    const submit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (!email.trim().length) {
        setError({ type: "email", value: "Please enter an email address." });
        document
          .getElementById("signupEmail")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      if (!emailRegex.test(email)) {
        setError({
          type: "email",
          value: "Please enter a valid email address.",
        });
        document
          .getElementById("signupEmail")
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
        return;
      }
      if (
        !is_agree_terms ||
        (dob && isUserBelowThirteenYears(dob) && !is_confirm_parental) // first need to check if user is below 13, if yes then need to check parental consent
      ) {
        document.getElementById("age-message")?.classList.add("age-validation"); // if user is below 13 and not confirm parental consent, then need to shake age validation message
        return;
      }
      if (!emailRegex.test(email)) {
        return;
      }
      try {
        loaderDispatch({ type: "update", payload: true });
        const res = await signUpEndPoint<{
          status: boolean;
          message: string;
          user: IUser;
        }>({
          user: {
            first_name,
            last_name,
            password,
            password_confirmation,
            email,
            is_agree_terms,
            is_confirm_parental:
              dob && isUserBelowThirteenYears(dob)
                ? is_confirm_parental
                : undefined,
            dob,
          },
        });
        if (res?.data?.status) {
          setSignUpModal({ payload: false, type: "updateSignup" });
          if (isMobile) {
            searchParams.delete("tab");
            setSearchParams(searchParams, { replace: true });
          }
          swal
            .fire({
              icon: "success",
              text: res.data.message,
              showCloseButton: false,
              buttonsStyling: true,
              showConfirmButton: true,
              confirmButtonText: "Ok",
              customClass: {
                popup: "confirmation-modal max-w-2xl w-full !p-6",
                confirmButton:
                  "confirm-button text-2xl p-[1.2rem] max-md:px-16 px-28 max-lg:px-2 bg-100 rounded-[5px_27px_19px_25px] bg-no-repeat bg-[url('@assets/images/btnBgImage.webp')] duration-150 hover:scale-95 text-black w-auto max-md:w-full max-lg:w-2/4",
              },
            })
            .then((result) => {
              if (result.isConfirmed) {
                //do nothing just close the popup
              }
            });
        }
      } catch (e: any) {
        setApiError(e);
      } finally {
        loaderDispatch({ type: "update", payload: false });
      }
    };

    const checkEmail = useCallback(
      async (email: string) => {
        try {
          const res = await checkEmailEnpoint<{
            status: boolean;
            message: string;
          }>({ email });
          if (res?.data?.status) {
            flashNotification({ status: false, message: res.data.message });
          }
        } catch (e) {
          setApiError(e);
        }
      },
      [setApiError, checkEmailEnpoint, flashNotification]
    );

    // for check if user is below 13 or not
    const isUserBelowThirteenYears = useCallback(
      (getUserSelectedValue: Date) => {
        const thirteenYearsAgo = subYears(new Date(), 13);
        const isValid =
          isAfter(getUserSelectedValue, thirteenYearsAgo) ||
          isEqual(getUserSelectedValue, thirteenYearsAgo);
        return isValid;
      },
      []
    );

    useEffect(() => {
      if (password.length) {
        setHasNumber(containsNumber(password));
        setHasLowerAndUpperChar(containsLowerCase(password));
        setHasLowerAndUpperChar(containsUpperCase(password));
        setHasEightChars(isLengthGreaterThanOrEqualToEight(password));
        setHasSpecialChar(checkIfContainsSpecialCharacter(password));
      }
    }, [password]);

    // useEffect(() => {
    //   let timer: ReturnType<typeof setTimeout>;
    //   if (error) {
    //     timer = setTimeout(() => {
    //       setError(null);
    //     }, 3000);
    //   }
    //   return () => {
    //     clearTimeout(timer);
    //   };
    // }, [error]);

    useEffect(() => {
      const header = document.getElementById("header");
      const footer = document.getElementById("footer");

      document.body.style.overflow = "hidden";
      if (header) {
        header.style.zIndex = "0";
      }
      if (footer) {
        footer.style.zIndex = "-1";
      }

      return () => {
        if (header) {
          header.style.zIndex = "";
        }
        if (footer) {
          footer.style.zIndex = "";
        }
        // Enable background scroll
        document.body.style.overflow = "auto";
      };
    }, []);

    const [scrollPosition, setScrollPosition] = useState(0);

    useEffect(() => {
      const header = document.getElementById("header");
      const footer = document.getElementById("footer");
      const currentScrollY = window.scrollY;
      setScrollPosition(currentScrollY);

      if (header) {
        header.style.zIndex = "0";
      }
      if (footer) {
        footer.style.zIndex = "-1";
      }
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${currentScrollY}px`;
      document.body.style.width = "100%";
      document.body.style.touchAction = "none";

      return () => {
        if (header) {
          header.style.zIndex = "";
        }
        if (footer) {
          footer.style.zIndex = "";
        }
        // Enable background scroll
        document.documentElement.style.overflow = "";
        document.body.style.overflow = "auto";
        document.body.style.position = "";
        document.body.style.width = "";
        document.body.style.touchAction = "";
        window.scrollTo(0, scrollPosition);
      };
    }, [scrollPosition]);

    return (
      <div
        className={`fixed w-full h-full bg-[#00000080] top-0 left-0 grid place-items-center z-[9999] modal-content overflow-hidden ${
          signUpModal ? "block" : "hidden"
        }`}
        id="signupModal"
      >
        <div className="overflow-auto w-full h-full">
          {isLoading && <TransparentLoader />}
          <div
            className="max-w-2xl mx-auto max-md:max-w-[95%] max-md:mx-auto w-full bg-gradient-to-b from-[#1D1159] to-[#00AEEF] relative rounded-[5px_30px_40px_0px]"
            id="sign-up-form"
            ref={ref}
            onAnimationEnd={() => {
              document
                .getElementById("sign-up-form")
                ?.classList.remove("form-shake");
            }}
          >
            <img
              src={modalBorder}
              className="absolute top-0 left-0 w-full h-full z-[0]"
              alt=""
            />
            <div className="relative z-10 py-10 px-32 max-md:px-4 max-md:py-6">
              <button
                onClick={() => {
                  setSignUpModal({ payload: false, type: "updateSignup" });
                  if (isMobile) {
                    searchParams.delete("tab");
                    setSearchParams(searchParams, { replace: true });
                  }
                }}
                className="text-primary absolute max-md:right-4 max-md:top-4 top-10 right-10 text-3xl max-md:text-2xl ml-auto block duration-150 hover:scale-95"
                aria-label="Close"
              >
                <i className="ri-close-circle-line"></i>
              </button>
              <h2 className="text-primary text-center text-3xl max-md:text-xl mb-2">
                Welcome to Fatsu
              </h2>
              <form onSubmit={submit} className="my-8 max-md:my-4">
                <div className="flex max-md:flex-col gap-3">
                  <div className="mb-6 max-sm:mb-2">
                    <label
                      className="block text-[#D854BB] text-base f-family-mochiy-pop mb-2"
                      htmlFor="name"
                    >
                      First Name
                    </label>
                    <div className="relative">
                      <input
                        id="name"
                        maxLength={25}
                        className="relative text-black z-10 text-base w-full f-family-mochiy-pop bg-transparent focus:outline-none focus:ring-1 focus:ring-transparent focus:border-transparent bg-primary z- p-[1.6rem] pr-12 max-md:pr-12 max-md:px-4 max-md:py-5 rounded-[5px_27px_19px_25px] bg-100 f-family-mochiy-pop"
                        type="text"
                        placeholder="Enter"
                        value={first_name}
                        onChange={(e) => setFirst_name(e.target.value)}
                      />
                      <img
                        className="absolute top-0 left-0 w-full h-full"
                        src={inputBgMd}
                        alt=""
                      />
                    </div>
                  </div>
                  <div className="mb-6 max-sm:mb-2">
                    <label
                      className="block text-[#D854BB] text-base f-family-mochiy-pop mb-2"
                      htmlFor="name"
                    >
                      Last Name
                    </label>
                    <div className="relative">
                      <input
                        id="lastName"
                        className="relative text-black z-10 text-base w-full f-family-mochiy-pop bg-transparent focus:outline-none focus:ring-1 focus:ring-transparent focus:border-transparent bg-primary z- p-[1.6rem] pr-12 max-md:pr-12 max-md:px-4 max-md:py-5 rounded-[5px_27px_19px_25px] bg-100 f-family-mochiy-pop"
                        maxLength={25}
                        type="text"
                        placeholder="Enter"
                        value={last_name}
                        onChange={(e) => setLast_name(e.target.value)}
                      />
                      <img
                        className="absolute top-0 left-0 w-full h-full"
                        src={inputBgMd}
                        alt=""
                      />
                    </div>
                  </div>
                </div>
                <div className="mb-6 max-sm:mb-2">
                  <label
                    className="block text-[#D854BB] text-base f-family-mochiy-pop mb-2"
                    htmlFor="signupEmail"
                  >
                    Email
                  </label>
                  <div className="relative">
                    <input
                      id="signupEmail"
                      className="relative text-black z-10 text-base w-full f-family-mochiy-pop bg-transparent focus:outline-none focus:ring-1 focus:ring-transparent focus:border-transparent bg-primary z- p-[1.6rem] pr-12 max-md:pr-12 max-md:px-4 max-md:py-5 rounded-[5px_27px_19px_25px] bg-100 f-family-mochiy-pop"
                      type="email"
                      autoComplete="email webauthn"
                      placeholder="Enter email address"
                      autoCapitalize="none"
                      inputMode="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value), setError(null);
                      }}
                      onBlur={async () => {
                        if (emailRegex.test(email)) await checkEmail(email);
                      }}
                    />
                    <img
                      className="absolute top-0 left-0 w-full h-full"
                      src={inputBgMd}
                      alt=""
                    />
                  </div>
                  {error?.type === "email" && error.value && (
                    <p className="text-red-500 mt-1 text-base font-medium f-family-mochiy-pop w-full">
                      {error?.type === "email" && error.value}
                    </p>
                  )}
                </div>
                <div className="mb-6 max-sm:mb-2">
                  <label
                    className="block text-[#D854BB] text-base f-family-mochiy-pop mb-2"
                    htmlFor="date"
                  >
                    DOB
                  </label>
                  <CustomDatePicker
                    value={dob}
                    setDob={(getUserSelectedDate) => {
                      setIs_confirm_parental(false);
                      setDob(getUserSelectedDate);
                    }}
                  />
                </div>
                <div className="mb-6 relative max-sm:mb-2">
                  <label
                    className="block text-[#D854BB] text-base f-family-mochiy-pop mb-2"
                    htmlFor="signupPassword"
                  >
                    Password
                  </label>
                  <div className="relative">
                    <input
                      onFocus={() => {
                        setIsPasswordValid(true);
                        setPasswordDropdown(true);
                      }}
                      ref={passwordRef}
                      autoComplete="password"
                      id="signupPassword"
                      className="relative text-black z-10 text-base w-full f-family-mochiy-pop bg-transparent focus:outline-none focus:ring-1 focus:ring-transparent focus:border-transparent bg-primary z- p-[1.6rem] pr-12 max-md:pr-12 max-md:px-4 max-md:py-5 rounded-[5px_27px_19px_25px] bg-100 f-family-mochiy-pop"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter password"
                      value={password}
                      onBlur={() => {
                        setIsPasswordValid(
                          containsNumber(password) &&
                            containsLowerCase(password) &&
                            containsUpperCase(password) &&
                            isLengthGreaterThanOrEqualToEight(password) &&
                            checkIfContainsSpecialCharacter(password)
                        );
                        setPasswordsMatch(password === password_confirmation);
                        setPasswordDropdown(false);
                      }}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <img
                      className="absolute top-0 left-0 w-full h-full"
                      src={inputBgMd}
                      alt=""
                    />
                    <button
                      type="button"
                      className="absolute right-5 top-2/4 -translate-y-2/4 z-[11]"
                      onClick={() => {
                        setShowPassword(!showPassword);
                      }}
                    >
                      <i
                        className={`${
                          showPassword ? "ri-eye-off-fill" : "ri-eye-fill"
                        } text-2xl`}
                      ></i>
                    </button>
                  </div>
                  {password.length > 0 && passwordDropdown && (
                    <div className="absolute bg-[#1d1159] w-full p-6 z-[12] rounded-[5px_27px_19px_25px] bg-[length:100%_100%] bg-no-repeat bg-[url('@assets/images/borders/video-border.webp')]">
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <span className="text-xl text-primary-gradient">
                          8 Characters
                        </span>
                        {isLengthGreaterThanOrEqualToEight(password) && (
                          <img src={checkbox} className="w-6 h-6" alt="" />
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <span className="text-xl text-primary-gradient">
                          1 Uppercase Letter
                        </span>
                        {containsUpperCase(password) && (
                          <img src={checkbox} className="w-6 h-6" alt="" />
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <span className="text-xl text-primary-gradient">
                          1 Lowercase Letter
                        </span>
                        {containsLowerCase(password) && (
                          <img src={checkbox} className="w-6 h-6" alt="" />
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <span className="text-xl text-primary-gradient">
                          1 number
                        </span>
                        {containsNumber(password) && (
                          <img src={checkbox} className="w-6 h-6" alt="" />
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="text-xl text-primary-gradient">
                          1 special Character
                        </span>
                        {checkIfContainsSpecialCharacter(password) && (
                          <img src={checkbox} className="w-6 h-6" alt="" />
                        )}
                      </div>
                    </div>
                  )}
                  {!isPasswordValid && password?.length > 0 && (
                    <span className="text-red-500 text-base font-medium mt-1 block f-family-mochiy-pop">
                      Password does not meet requirements
                    </span>
                  )}
                </div>
                <div>
                  <label
                    className="block text-[#D854BB] text-base f-family-mochiy-pop mb-2"
                    htmlFor="signupConfPass"
                  >
                    Confirm Password
                  </label>
                  <div className="relative">
                    <input
                      id="signupConfPass"
                      autoComplete="confirm-password"
                      className="relative text-black z-10 text-base w-full f-family-mochiy-pop bg-transparent focus:outline-none focus:ring-1 focus:ring-transparent focus:border-transparent bg-primary z- p-[1.6rem] pr-12 max-md:pr-12 max-md:px-4 max-md:py-5 rounded-[5px_27px_19px_25px] bg-100 f-family-mochiy-pop"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Re-enter password"
                      value={password_confirmation}
                      onChange={(e) => setPassword_confirmation(e.target.value)}
                      onFocus={() => {
                        setPasswordsMatch(true);
                      }}
                      onBlur={() => {
                        setPasswordsMatch(password === password_confirmation);
                      }}
                    />
                    <img
                      className="absolute top-0 left-0 w-full h-full"
                      src={inputBgMd}
                      alt=""
                    />
                    <button
                      type="button"
                      className="absolute right-5 top-2/4 -translate-y-2/4 z-[11]"
                      onClick={() => {
                        setShowConfirmPassword(!showConfirmPassword);
                      }}
                    >
                      <i
                        className={` ${
                          showConfirmPassword
                            ? "ri-eye-off-fill"
                            : "ri-eye-fill"
                        } text-2xl`}
                      ></i>
                    </button>
                  </div>
                  {!passwordsMatch && password_confirmation?.length > 0 && (
                    <span className="text-red-500 text-base font-medium f-family-mochiy-pop mt-1">
                      Passwords do not match
                    </span>
                  )}
                </div>
                <div className="my-5 max-md:my-3">
                  {/* if user is below 13, then show this message, otherwise hide this message */}
                  {dob && isUserBelowThirteenYears(dob) && (
                    <div
                      id="age-message"
                      className="flex items-start mb-4"
                      onAnimationEnd={() => {
                        document
                          .getElementById("age-message")
                          ?.classList.remove("age-validation");
                      }}
                    >
                      <input
                        id="parental-checkbox"
                        type="checkbox"
                        checked={is_confirm_parental}
                        onChange={(e) =>
                          setIs_confirm_parental(e.target.checked)
                        }
                        className="peer custom-checkbox mt-1"
                      />
                      <label
                        htmlFor="parental-checkbox"
                        className="w-full ms-3 text-lg max-md:text-base f-family-mochiy-pop text-white"
                      >
                        By signing up, you confirm to have received parental
                        consent if you are below the age of 13.
                      </label>
                    </div>
                  )}

                  <div className="flex items-start mb-4">
                    <input
                      id="terms-checkbox"
                      type="checkbox"
                      value=""
                      className="peer custom-checkbox mt-1"
                      checked={is_agree_terms}
                      onChange={(e) => setIs_agree_terms(e.target.checked)}
                    />
                    <label
                      htmlFor="terms-checkbox"
                      className="w-[calc(100%-24px)] ms-3 text-base max-sm:text-sm leading-7 md:mt-[-5px] max-md:leading-7 max-sm:leading-7 f-family-mochiy-pop text-white"
                    >
                      By signing up, you agree to fatSu's
                      <span
                        role="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(
                            import.meta.env.VITE_REACT_LOCAL_HOST +
                              routes.terms,
                            "_blank",
                            "noopener"
                          );
                        }}
                        className="text-[#1D1159]"
                        aria-label="Terms and Condition"
                      >
                        {" "}
                        Terms of use
                      </span>{" "}
                      and
                      <span
                        role="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          window.open(
                            import.meta.env.VITE_REACT_LOCAL_HOST +
                              routes.policy,
                            "_blank",
                            "noopener"
                          );
                        }}
                        className="text-[#1D1159]"
                        aria-label="Privacy Policy"
                      >
                        {" "}
                        Privacy Policy.
                      </span>
                    </label>
                  </div>
                </div>
                <div className="px-7 max-sm:px-0">
                  <button
                    disabled={
                      !passwordsMatch ||
                      !isPasswordValid ||
                      !hasEightChars ||
                      !hasLowerAndUpperChar ||
                      !hasNumber ||
                      !hasSpecialChar ||
                      !(
                        Boolean(dob) &&
                        Boolean(first_name) &&
                        Boolean(last_name) &&
                        Boolean(password_confirmation)
                      ) ||
                      !is_agree_terms
                    }
                    type="submit"
                    className="w-full mt-2 text-2xl max-md:text-xl customized-button rounded-[5px_20px_11px_20px] px-10"
                    aria-label="Signup"
                  >
                    Sign Up
                  </button>
                </div>
              </form>
              <h5 className="text-lg max-sm:text-base text-center block text-white signup-form">
                ALREADY HAVE AN ACCOUNT?{" "}
                <button
                  className="inline-block duration-150 text-[#1D1159] hover:text-primary"
                  onClick={() => {
                    setLoginModal({ type: "updateLogin", payload: true });
                    if (isMobile) {
                      searchParams.set("tab", "login");
                      setSearchParams(searchParams, { replace: true });
                    }
                    setSignUpModal({ type: "updateSignup", payload: false });
                  }}
                  aria-label="Signin"
                >
                  SIGN IN
                </button>
              </h5>
            </div>
          </div>
        </div>
      </div>
    );
  }
);

export default Signup;
